import { ChangeEvent, CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import { Colors, TextAreaInput, TextAreaInputProps } from "main";

// Note: This stylesheet is used to apply hover states.
import "./Checkbox.css";

const ICON_SIZE = 18;

/********* Styles ********/
const styles = {
  container: ({
    isDisabled,
    descriptionText,
    hasBorderBottom,
    hasInputField,
    isReadOnly,
  }: {
    isDisabled: boolean | undefined;
    isReadOnly: boolean | undefined;
    descriptionText: string | undefined;
    hasBorderBottom: boolean;
    hasInputField: boolean;
  }) =>
    ({
      display: "flex",
      alignItems: !descriptionText && !hasInputField ? "center" : "start",
      columnGap: 16,
      justifyContent: "flex-start",
      background: Colors.white200,
      cursor: isDisabled || isReadOnly ? "not-allowed" : "pointer",
      padding: "16px 24px",
      borderBottom: hasBorderBottom ? `1px solid ${Colors.slate200}` : "none",
    }) as CSSProperties,
  iconWrapper: ({
    descriptionText,
    hasInputField,
  }: {
    descriptionText: string | undefined;
    hasInputField: boolean;
  }) =>
    ({
      display: "flex",
      alignItems: "center",
      padding: descriptionText || hasInputField ? "0 3px 3px 3px" : "3px",
      cursor: "inherit",
    }) as CSSProperties,
  icon: ({
    isDisabled,
    isChecked,
    isReadOnly,
  }: {
    isDisabled?: boolean;
    isChecked?: boolean;
    isReadOnly?: boolean;
  }): CSSProperties => {
    const borderColor = isDisabled ? Colors.slate300 : isReadOnly ? Colors.black : Colors.blue500;

    const backgroundColor = isDisabled
      ? isChecked
        ? Colors.slate300
        : Colors.white200
      : isChecked
        ? isReadOnly
          ? Colors.black
          : Colors.blue500
        : Colors.white200;

    return {
      width: ICON_SIZE,
      height: ICON_SIZE,
      position: "relative",
      transition: "background 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      borderRadius: `calc(${ICON_SIZE}px * 0.125)`,
      border: `2px solid ${borderColor}`,
      background: backgroundColor,
      boxSizing: "border-box",
    } as CSSProperties;
  },
  iconPath: ({ isChecked = false }: { isChecked?: boolean }) =>
    ({
      fill: "none",
      stroke: Colors.white200,
      strokeWidth: "3",
      opacity: isChecked ? "1" : "0",
      strokeDasharray: "30",
      strokeDashoffset: isChecked ? "0" : "30",
      ...(isChecked && { transition: "stroke-dashoffset 90ms linear 90ms" }),
    }) as CSSProperties,
  descriptionText: ({
    descriptionTextState,
  }: {
    descriptionTextState: "primary" | "secondary" | undefined;
  }) =>
    ({
      color: descriptionTextState === "secondary" ? Colors.slate400 : Colors.black,
      fontSize: 12,
      margin: 0,
    }) as CSSProperties,
};

/********* Types ********/
type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
  label: ReactNode;
  isChecked?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  descriptionText?: string;
  descriptionTextState?: "primary" | "secondary";
  hasInputField?: boolean;
  textAreaProps?: TextAreaInputProps;
  hasBorderBottom?: boolean;
  onCheckedChange?: (value: boolean) => void;
};

/**
 * Component | Checkbox
 *
 * A checkbox component with optional label, description, and input fields.
 *
 * Note: Hover states are handled in `Checkbox.css`, so any wrapper
 * or parent components may need their own hover styles
 * if additional styling is required.
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=11456-2763&m
 */
export function Checkbox(props: CheckboxProps) {
  const {
    isDisabled,
    isChecked,
    isReadOnly,
    label,
    onCheckedChange,
    descriptionText,
    descriptionTextState = "primary",
    hasInputField = false,
    hasBorderBottom = false,
    textAreaProps,
  } = props;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isDisabled && !isReadOnly) onCheckedChange?.(e.target.checked);
  };

  /********* Render ********/
  return (
    <label
      style={styles.container({
        isDisabled,
        descriptionText,
        hasBorderBottom,
        hasInputField,
        isReadOnly,
      })}
      className={!isDisabled ? "checkbox-container" : undefined}
    >
      <input
        data-testid={
          typeof label === "string"
            ? `checkbox-${label.toLowerCase().replace(/ /g, "-")}`
            : "checkbox"
        }
        type="checkbox"
        checked={isChecked}
        onChange={handleOnChange}
        disabled={isDisabled}
        readOnly={isReadOnly}
        style={{ display: "none" }}
      />
      <div style={styles.iconWrapper({ descriptionText, hasInputField })}>
        <CheckIcon {...props}></CheckIcon>
      </div>
      <div style={{ width: "100%" }}>
        {!descriptionText ? (
          <>{label}</>
        ) : (
          <>
            {label}
            <p style={styles.descriptionText({ descriptionTextState })}>{descriptionText}</p>
          </>
        )}
        {hasInputField && isChecked && textAreaProps && (
          <div style={{ paddingTop: 16 }}>
            <TextAreaInput {...textAreaProps} />
          </div>
        )}
      </div>
    </label>
  );
}

/********* Helper Components ********/
function CheckIcon(props: CheckboxProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      style={styles.icon(props)}
      className={!props.isChecked && !props.isDisabled ? "checkbox-icon" : undefined}
    >
      <path style={styles.iconPath(props)} d="M1.73,12.91 8.1,19.28 22.79,4.59"></path>
    </svg>
  );
}
